/**
 * What happened since the last check-in (TIMELINE R27): the payments the map expected, what the
 * person says about them, and the rows a check-in stores. Web, Claude and the server share these,
 * so step 1 lists the same payments wherever a check-in is made.
 */
import {
  assets as assetsNs, contract, core, debt as debtNs, formatDay, investments as investmentsNs, parseDay, schedule,
  type Day, type Decimal, type Plan, type Transaction, type Wire,
} from '@map-my-money/engine';
import { changedFrom } from './dated.ts';
import { money } from './money.ts';
import { decode, orderedWaypoints, planAsOf, type WaypointInput, type WirePlan } from './run.ts';
import { readableDate } from './stamp.ts';

export type FlowKind = 'income' | 'expense' | 'investment' | 'debt';
export type FlowStatus = 'confirmed' | 'changed' | 'skipped' | 'added';

/** The longest stretch step 1 lists: a check-in reviews at most the 12 months before it. */
export const REVIEW_MONTHS = 12;

/** At most this many changed, skipped and added payments in one check-in. */
export const MAX_FLOWS = 200;

export const MAX_FLOW_LABEL = 80;

/** One payment the map expected (R27): the key names it by item, day and its place among that item's payments that day. */
export interface ExpectedFlow {
  readonly key: string;
  readonly item: string;
  readonly label: string;
  readonly kind: FlowKind;
  readonly date: string;
  readonly seq: number;
  /** What moves in or out of cash, positive: a debt's whole payment, your own investment payment, what a withdrawal pays out. */
  readonly amount: string;
  readonly direction: 'in' | 'out';
  /** A debt payment's interest at the rate that day, and the rest. */
  readonly interest?: string;
  readonly principal?: string;
  /** An investment payment's employer match and tax relief, on top of your own amount. */
  readonly employer?: string;
  readonly relief?: string;
}

/** A row stored with a check-in: every payment step 1 showed, and every one-off added, with its label and expected amount frozen. */
export interface CheckInFlow {
  /** The row's place in the check-in. */
  readonly seq: number;
  /** null for an added one-off. */
  readonly item: string | null;
  /** The payment's place among the item's payments that day (0 for the first). */
  readonly occurrence: number;
  readonly kind: FlowKind;
  readonly label: string;
  readonly date: string;
  /** null when added. */
  readonly expected: string | null;
  /** What was paid, positive; "0" when skipped. */
  readonly actual: string;
  readonly status: FlowStatus;
}

export interface PaymentRef {
  readonly item: string;
  readonly date: string;
  readonly seq?: number | undefined;
}

/** What a check-in body says happened (R27): only the exceptions. Absent: step 1 was not reviewed. */
export interface CheckInFlowsBody {
  /** The first day of the window the person reviewed; the server's own when left out. */
  readonly from?: string;
  readonly changed?: readonly (PaymentRef & { readonly amount: string; readonly fromNowOn?: boolean })[];
  readonly skipped?: readonly PaymentRef[];
  /** `everyMonth`: it carries on, so the map gains it monthly from its first day after the check-in. */
  readonly added?: readonly { readonly kind: 'income' | 'expense'; readonly label: string; readonly date: string; readonly amount: string; readonly everyMonth?: boolean }[];
}

/** One-offs a check-in added are on the map under these ids (run.ts withCheckInFlows). */
export const ADDED_PREFIX = 'added:';

export const flowKey = (item: string, date: string, seq = 0): string => `${item}|${date}|${seq}`;

const dayAfter = (day: Day): string => formatDay(core.addDays(day, 1));

/**
 * The days a check-in on `date` reviews: after the previous check-in's day (the latest dated before
 * it) and the setup day, through `date`, at most the 12 months before it. `empty` when nothing fits.
 */
export function checkInWindow(wire: WirePlan, waypoints: readonly WaypointInput[], date: string): { readonly from: string; readonly to: string; readonly previous: string | null; readonly empty: boolean } {
  const day = parseDay(date);
  const previous = orderedWaypoints(waypoints).filter((w) => w.date < date).at(-1)?.date ?? null;
  const setup = wire.configuration.initialBalanceDate;
  const bounds = [core.addMonths(day, -REVIEW_MONTHS), ...(setup ? [parseDay(setup)] : []), ...(previous ? [parseDay(previous)] : [])];
  const after = bounds.reduce((a, b) => (b > a ? b : a));
  return { from: dayAfter(after), to: date, previous, empty: after >= day };
}

const ofDay = (t: Transaction, day: Day) => ({ t, day });

/**
 * Every non-zero payment of the map's items dated in [from, to] (R21, R23, R27), at what each pays on
 * its day: dated amounts and rises, a debt's payment at what its balance takes with the interest at
 * the rate that day, an investment's match and relief. `plan` is the map as the check-ins left it.
 */
export function paymentsBetween(plan: Plan, from: Day, to: Day, today: Day): ExpectedFlow[] {
  const end = core.addDays(to, 1);
  const transactions = assetsNs.settleSoldAssetDebts(plan.transactions.filter((t) => t.isActive), assetsNs.planSaleDates(plan), { onlyDebts: false });
  const incomes = transactions.filter((t) => t.type === 'income');
  const withdrawn = (t: Transaction) => new Map(investmentsNs.runPot(t, incomes, plan.configuration.expectedReturnPercent, [], to).payments.map((p) => [p.date, core.negate(p.cash)]));
  const events = transactions.flatMap((t) => {
    if (t.type === 'debt') return debtNs.debtPaymentDates(t, end).filter((d) => d >= from).map((day) => ofDay(t, day));
    return schedule.scheduledDates(t, end).filter((d) => d >= from).map((day) => ofDay(t, day));
  });
  const order = new Map(transactions.map((t, i) => [t.id, t.type === 'debt' ? transactions.length + i : i]));
  events.sort((a, b) => a.day - b.day || order.get(a.t.id)! - order.get(b.t.id)!);
  const seen = new Map<string, number>();
  const pots = new Map<string, Map<Day, Decimal>>();
  return events.flatMap(({ t, day }): ExpectedFlow[] => {
    const date = formatDay(day);
    const seq = seen.get(`${t.id}|${date}`) ?? 0;
    seen.set(`${t.id}|${date}`, seq + 1);
    const base = { key: flowKey(t.id, date, seq), item: t.id, label: t.label, kind: t.type as FlowKind, date, seq };
    if (t.type === 'debt') {
      const paid = core.round2(debtNs.debtPayment(t, day, today));
      if (!core.gt(paid, core.ZERO)) return [];
      const beforeBalanceDay = t.debtEntryMode === 'currentBalance' && t.debtCurrentBalance !== null && day < debtNs.balanceDay(t, today);
      if (beforeBalanceDay) return [{ ...base, amount: money(paid), direction: 'out' }];
      const owed = debtNs.currentBalance(t, core.addDays(day, -1), today);
      const interest = core.round2(core.mul(owed, debtNs.monthlyRate(schedule.effectiveInterestRate(t, day) ?? core.ZERO)));
      return [{ ...base, amount: money(paid), direction: 'out', interest: money(interest), principal: money(core.sub(paid, interest)) }];
    }
    const own = schedule.paidOn(t, day);
    if (t.type === 'investment' && core.lt(own, core.ZERO)) {
      const pot = pots.get(t.id) ?? withdrawn(t);
      pots.set(t.id, pot);
      const out = core.round2(pot.get(day) ?? core.ZERO);
      return core.gt(out, core.ZERO) ? [{ ...base, amount: money(out), direction: 'in' }] : [];
    }
    if (core.isZero(own)) return [];
    if (t.type !== 'investment') return [{ ...base, amount: money(own), direction: t.type === 'income' ? 'in' : 'out' }];
    const employer = investmentsNs.employerMatchContribution(t, incomes, day, own);
    const relief = core.sub(core.sub(investmentsNs.calculateTotalInvestment(t, day, incomes), own), employer);
    return [{
      ...base, amount: money(own), direction: 'out',
      ...(core.isZero(employer) ? {} : { employer: money(employer) }),
      ...(core.isZero(core.round2(relief)) ? {} : { relief: money(relief) }),
    }];
  });
}

/** Step 1's list for a check-in covering [from, to]: the map as the check-ins before `to` left it (R27). */
export function expectedFlows(wire: WirePlan, waypoints: readonly WaypointInput[], from: string, to: string, today: string = to): ExpectedFlow[] {
  if (from > to) return [];
  const before = waypoints.filter((w) => w.date < to);
  return paymentsBetween(planAsOf(decode(wire), before, to), parseDay(from), parseDay(to), parseDay(today)).filter((e) => !e.item.startsWith(ADDED_PREFIX));
}

/** The rows a check-in stores: every payment it showed (as expected unless the body says otherwise), then every one-off added. */
export function checkInFlowRows(expected: readonly ExpectedFlow[], body: CheckInFlowsBody): CheckInFlow[] {
  const changed = new Map((body.changed ?? []).map((c) => [flowKey(c.item, c.date, c.seq), c.amount]));
  const skipped = new Set((body.skipped ?? []).map((s) => flowKey(s.item, s.date, s.seq)));
  const shown = expected.map((e, i): CheckInFlow => {
    const amount = changed.get(e.key);
    const status: FlowStatus = skipped.has(e.key) ? 'skipped' : amount !== undefined ? 'changed' : 'confirmed';
    return {
      seq: i, item: e.item, occurrence: e.seq, kind: e.kind, label: e.label, date: e.date, expected: e.amount,
      actual: status === 'skipped' ? '0' : status === 'changed' ? money(contract.decodeDecimal(amount!)) : e.amount, status,
    };
  });
  const added = (body.added ?? []).map((a, i): CheckInFlow => ({
    seq: shown.length + i, item: null, occurrence: 0, kind: a.kind, label: a.label.trim(), date: a.date, expected: null, actual: money(contract.decodeDecimal(a.amount)), status: 'added',
  }));
  return [...shown, ...added];
}

/** Only what differs from the map: the body that would store these rows. */
export function flowsBodyOf(rows: readonly CheckInFlow[], from: string): CheckInFlowsBody {
  return {
    from,
    changed: rows.filter((r) => r.status === 'changed').map((r) => ({ item: r.item!, date: r.date, ...(r.occurrence ? { seq: r.occurrence } : {}), amount: r.actual })),
    skipped: rows.filter((r) => r.status === 'skipped').map((r) => ({ item: r.item!, date: r.date, ...(r.occurrence ? { seq: r.occurrence } : {}) })),
    added: rows.filter((r) => r.status === 'added').map((r) => ({ kind: r.kind as 'income' | 'expense', label: r.label, date: r.date, amount: r.actual })),
  };
}

/** The item's first payment day after `date`, or null when it pays nothing more. */
export function nextPaymentDay(t: Wire.Transaction, date: string): string | null {
  const decoded = contract.decodeTransaction(t);
  const after = parseDay(date);
  const horizon = core.addMonths(after, 13);
  const days = t.type === 'debt' ? debtNs.debtPaymentDates(decoded, horizon) : schedule.scheduledDates(decoded, horizon);
  const next = days.find((d) => d > after);
  return next === undefined ? null : formatDay(next);
}

export interface MapChange {
  readonly item: string;
  readonly label: string;
  readonly amount: string;
  readonly from: string;
  /** changed: an amount from now on; added: a one-off that happens every month. */
  readonly kind: 'changed' | 'added';
}

const slug = (label: string): string => label.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'item';

/** The first day after `after` on a monthly schedule anchored on `day`: when a one-off ticked "every month" joins the map. */
export const monthlyAfter = (day: string, after: string): string => {
  const start = parseDay(day);
  let n = 1;
  while (core.addMonths(start, n) <= parseDay(after)) n += 1;
  return formatDay(core.addMonths(start, n));
};

/**
 * What a check-in changes on the map (R27, R24), saved with it as one plan version: every changed
 * amount ticked "from now on", from its item's next payment day after the check-in, and every added
 * one-off that happens every month, as a monthly item from its first day after the check-in. Null when
 * nothing is ticked. `changesFrom` names the day of each changed item.
 */
export function fromNowOn(wire: WirePlan, body: CheckInFlowsBody, date: string): { readonly plan: WirePlan; readonly changesFrom: Readonly<Record<string, string>>; readonly changes: readonly MapChange[] } | null {
  const ticked = (body.changed ?? []).filter((c) => c.fromNowOn);
  const monthly = (body.added ?? []).filter((a) => a.everyMonth);
  if (ticked.length === 0 && monthly.length === 0) return null;
  const changesFrom: Record<string, string> = {};
  const changes: MapChange[] = [];
  const transactions = (wire.transactions ?? []).map((t) => {
    const c = ticked.find((x) => x.item === t.id);
    const from = c ? nextPaymentDay(t, date) : null;
    if (!c || from === null) return t;
    changesFrom[t.id] = from;
    changes.push({ item: t.id, label: t.label ?? t.id, amount: money(contract.decodeDecimal(c.amount)), from, kind: 'changed' });
    return changedFrom(t, { ...t, amount: money(contract.decodeDecimal(c.amount)) }, from);
  });
  const ids = new Set(transactions.map((t) => t.id));
  for (const a of monthly) {
    let id = slug(a.label);
    for (let n = 2; ids.has(id); n++) id = `${slug(a.label)}-${n}`;
    ids.add(id);
    const label = a.label.trim();
    const from = monthlyAfter(a.date, date);
    const amount = money(contract.decodeDecimal(a.amount));
    transactions.push({ id, label, type: a.kind, amount, frequency: 'monthly', startDate: from });
    changes.push({ item: id, label, amount, from, kind: 'added' });
  }
  return changes.length === 0 ? null : { plan: { ...wire, transactions }, changesFrom, changes };
}

export type FlowGroupId = 'in' | 'invested' | 'spent' | 'debts';

/** One payment as step 1 shows it: what the map expected and what the person says. */
export interface FlowPayment extends ExpectedFlow {
  readonly status: Exclude<FlowStatus, 'added'>;
  /** What was paid: the expected amount unless changed, 0 when skipped. */
  readonly actual: string;
}

/** One item in a step 1 group, with its payments, or a one-off added. */
export interface FlowItem {
  readonly item: string;
  readonly label: string;
  readonly kind: FlowKind;
  readonly payments: readonly FlowPayment[];
  /** Set for a one-off added: its place in the body's `added`. */
  readonly added?: { readonly index: number; readonly date: string; readonly amount: string; readonly everyMonth: boolean };
  /** What it actually moved, by what the person says. */
  readonly total: string;
  readonly changed: number;
  readonly skipped: number;
}

export interface FlowGroup {
  readonly id: FlowGroupId;
  readonly items: readonly FlowItem[];
  readonly total: string;
  /** Payments listed, and how many of them happened (added ones included). */
  readonly count: number;
  readonly happened: number;
}

const GROUP_OF = (f: Pick<ExpectedFlow, 'kind' | 'direction'>): FlowGroupId =>
  f.kind === 'debt' ? 'debts' : f.kind === 'investment' ? (f.direction === 'in' ? 'in' : 'invested') : f.kind === 'income' ? 'in' : 'spent';

const GROUP_ORDER: readonly FlowGroupId[] = ['in', 'invested', 'spent', 'debts'];

/**
 * Step 1's screen model (R27): the expected payments grouped as money in, investments, spending and
 * debt payments, one item per row with its payments, each as the body says it went, plus the one-offs
 * added; items and groups carry what actually moved. Biggest items first.
 */
export function flowGroups(expected: readonly ExpectedFlow[], body: CheckInFlowsBody): FlowGroup[] {
  const changed = new Map((body.changed ?? []).map((c) => [flowKey(c.item, c.date, c.seq), c.amount]));
  const skipped = new Set((body.skipped ?? []).map((x) => flowKey(x.item, x.date, x.seq)));
  const decimal = (text: string) => contract.decodeDecimal(text);
  const items = new Map<string, FlowItem & { payments: FlowPayment[] }>();
  for (const e of expected) {
    const amount = changed.get(e.key);
    const status: FlowPayment['status'] = skipped.has(e.key) ? 'skipped' : amount !== undefined ? 'changed' : 'confirmed';
    const payment: FlowPayment = { ...e, status, actual: status === 'skipped' ? '0' : status === 'changed' ? money(decimal(amount!)) : e.amount };
    const id = `${GROUP_OF(e)}|${e.item}`;
    const current = items.get(id) ?? { item: e.item, label: e.label, kind: e.kind, payments: [], total: '0', changed: 0, skipped: 0 };
    current.payments.push(payment);
    items.set(id, {
      ...current,
      total: money(core.add(decimal(current.total), decimal(payment.actual))),
      changed: current.changed + (status === 'changed' ? 1 : 0),
      skipped: current.skipped + (status === 'skipped' ? 1 : 0),
    });
  }
  (body.added ?? []).forEach((a, index) => {
    items.set(`${a.kind === 'income' ? 'in' : 'spent'}|${ADDED_PREFIX}${index}`, {
      item: `${ADDED_PREFIX}${index}`, label: a.label, kind: a.kind, payments: [],
      added: { index, date: a.date, amount: money(decimal(a.amount)), everyMonth: a.everyMonth ?? false }, total: money(decimal(a.amount)), changed: 0, skipped: 0,
    });
  });
  return GROUP_ORDER.map((id): FlowGroup => {
    const inGroup = [...items.entries()].filter(([key]) => key.startsWith(`${id}|`)).map(([, item]) => item);
    const listed = [...inGroup].sort((a, b) => Number(Boolean(a.added)) - Number(Boolean(b.added)) || core.compare(decimal(b.total), decimal(a.total)) || (b.payments.length - a.payments.length));
    const count = listed.reduce((n, i) => n + (i.added ? 1 : i.payments.length), 0);
    return {
      id, items: listed, count,
      happened: count - listed.reduce((n, i) => n + i.skipped, 0),
      total: money(listed.reduce<Decimal>((t, i) => core.add(t, decimal(i.total)), core.ZERO)),
    };
  }).filter((g) => g.items.length > 0);
}

export interface CheckInFlowIssue {
  readonly path: string;
  readonly reason: 'flows-from' | 'flow-outside' | 'flow-unknown' | 'flow-duplicate' | 'flow-amount' | 'flow-label' | 'too-many-flows' | 'from-now-on';
  readonly message: string;
}

/**
 * R27's refusals: a window other than the one the server lists, a flow outside it, a payment the map
 * did not expect or named twice, an added one-off without a label or an amount above 0, a changed
 * amount below 0, too many flows, and "from now on" on an item that pays nothing more.
 */
export function checkInFlowIssues(wire: WirePlan, waypoints: readonly WaypointInput[], date: string, body: CheckInFlowsBody, today: string): CheckInFlowIssue[] {
  const issues: CheckInFlowIssue[] = [];
  const window = checkInWindow(wire, waypoints, date);
  const within = (day: string) => !window.empty && day >= window.from && day <= window.to;
  const range = window.empty ? 'nothing (there is nothing to review since the last check-in)' : `${readableDate(window.from)} to ${readableDate(window.to)}`;
  if (body.from !== undefined && !window.empty && body.from !== window.from) {
    issues.push({ path: '/flows/from', reason: 'flows-from', message: `The payments since your last check-in have changed (they now start on ${readableDate(window.from)}). Open the check-in again to see them.` });
  }
  const count = (body.changed?.length ?? 0) + (body.skipped?.length ?? 0) + (body.added?.length ?? 0);
  if (count > MAX_FLOWS) issues.push({ path: '/flows', reason: 'too-many-flows', message: `A check-in can record at most ${MAX_FLOWS} changed, skipped and added payments.` });
  const expected = new Set(expectedFlows(wire, waypoints, window.from, window.to, today).map((e) => e.key));
  const named = new Set<string>();
  const checkRef = (path: string, ref: PaymentRef) => {
    const key = flowKey(ref.item, ref.date, ref.seq);
    if (!within(ref.date)) issues.push({ path, reason: 'flow-outside', message: `${ref.item} on ${readableDate(ref.date)} is outside this check-in, which covers ${range}.` });
    else if (!expected.has(key)) issues.push({ path, reason: 'flow-unknown', message: `Your map has no payment of ${ref.item} on ${readableDate(ref.date)}.` });
    if (named.has(key)) issues.push({ path, reason: 'flow-duplicate', message: `${ref.item} on ${readableDate(ref.date)} is named more than once.` });
    named.add(key);
  };
  (body.changed ?? []).forEach((c, i) => {
    checkRef(`/flows/changed/${i}`, c);
    if (core.lt(contract.decodeDecimal(c.amount), core.ZERO)) issues.push({ path: `/flows/changed/${i}/amount`, reason: 'flow-amount', message: 'A payment can’t be less than 0: say it didn’t happen instead.' });
    if (c.fromNowOn && within(c.date)) {
      const item = (wire.transactions ?? []).find((t) => t.id === c.item);
      if (item && nextPaymentDay(item, date) === null) issues.push({ path: `/flows/changed/${i}/fromNowOn`, reason: 'from-now-on', message: `${item.label ?? item.id} has no payment after ${readableDate(date)} to change from.` });
    }
  });
  (body.skipped ?? []).forEach((s, i) => checkRef(`/flows/skipped/${i}`, s));
  (body.added ?? []).forEach((a, i) => {
    const path = `/flows/added/${i}`;
    if (!within(a.date)) issues.push({ path: `${path}/date`, reason: 'flow-outside', message: `${a.label || 'An added payment'} on ${readableDate(a.date)} is outside this check-in, which covers ${range}.` });
    const label = a.label.trim();
    if (label.length === 0 || label.length > MAX_FLOW_LABEL) issues.push({ path: `${path}/label`, reason: 'flow-label', message: `Give what happened a name (up to ${MAX_FLOW_LABEL} characters).` });
    if (!core.gt(contract.decodeDecimal(a.amount), core.ZERO)) issues.push({ path: `${path}/amount`, reason: 'flow-amount', message: 'An added payment needs an amount above 0.' });
  });
  return issues;
}
